import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from 'contexts/BookingContext';
import { useAuth } from 'contexts/AuthContext';
import { getAvailability, getConfig } from 'services/mewsService';
import Toast from 'components/ux/toast/Toast';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function pad(n) {
  return String(n).padStart(2, '0');
}

function dateStr(year, month, day) {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

const Calendar = () => {
  const { state, dispatch } = useBooking();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const today = new Date();
  const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [availByCat, setAvailByCat] = useState(null); // { catId: [day1, day2, ...] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const config = state.config;

  // Config may not be loaded if the agent lands here directly
  useEffect(() => {
    if (config) return;
    (async () => {
      try {
        const cfg = await getConfig();
        dispatch({ type: 'SET_CONFIG', config: cfg });
      } catch (err) {
        if (err.status === 401) {
          logout();
          navigate('/login');
          return;
        }
        setError(`Failed to load configuration: ${err.message}`);
      }
    })();
  }, [config, dispatch, logout, navigate]);

  const fetchMonth = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const startUtc = `${dateStr(year, month, 1)}T00:00:00Z`;
      const nextMonth = month === 11 ? { y: year + 1, m: 0 } : { y: year, m: month + 1 };
      const endUtc = `${dateStr(nextMonth.y, nextMonth.m, 1)}T00:00:00Z`;
      const result = await getAvailability(startUtc, endUtc);
      const map = {};
      (result.availability?.CategoryAvailabilities || []).forEach((ca) => {
        const catId = ca.CategoryId || ca.ResourceCategoryId;
        map[catId] = ca.Availabilities || [];
      });
      setAvailByCat(map);
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      setError(`Failed to load availability: ${err.message}`);
      setAvailByCat(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, logout, navigate]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  const categories = config?.categories || [];

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  const goPrev = () => {
    if (isCurrentMonth) return;
    if (month === 0) { setYear(year - 1); setMonth(11); } else { setMonth(month - 1); }
  };
  const goNext = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); } else { setMonth(month + 1); }
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Monday-first: JS getDay() is 0=Sun..6=Sat
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;

  const availForDay = (day) => {
    if (!availByCat) return null;
    if (selectedCategory !== 'all') {
      const arr = availByCat[selectedCategory];
      return arr && arr.length >= day ? (arr[day - 1] ?? 0) : null;
    }
    let total = 0;
    let any = false;
    categories.forEach((cat) => {
      const arr = availByCat[cat.id];
      if (arr && arr.length >= day) {
        total += arr[day - 1] ?? 0;
        any = true;
      }
    });
    return any ? total : null;
  };

  const breakdownForDay = (day) => {
    if (!availByCat) return [];
    return categories.map((cat) => ({
      name: cat.name,
      avail: availByCat[cat.id]?.[day - 1] ?? null,
    }));
  };

  const handleDayClick = (ds, isPast) => {
    if (isPast) return;
    if (!checkIn || (checkIn && checkOut)) {
      // start a new selection
      setCheckIn(ds);
      setCheckOut('');
    } else if (ds > checkIn) {
      setCheckOut(ds);
    } else {
      // clicked same day or earlier — restart selection
      setCheckIn(ds);
      setCheckOut('');
    }
  };

  const handleSearch = () => {
    if (!checkIn || !checkOut) return;
    dispatch({ type: 'SET_DATES', checkIn: new Date(checkIn), checkOut: new Date(checkOut) });
    navigate('/availability');
  };

  const nights = checkIn && checkOut
    ? Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
    : 0;

  const cellColor = (avail, isPast) => {
    if (isPast) return 'bg-gray-50 text-gray-300';
    if (avail === null) return 'bg-white';
    if (avail === 0) return 'bg-red-50';
    if (avail <= 2) return 'bg-amber-50';
    return 'bg-green-50';
  };

  const badgeColor = (avail) => {
    if (avail === 0) return 'text-red-700';
    if (avail <= 2) return 'text-amber-700';
    return 'text-green-700';
  };

  // Build grid cells: leading blanks + days
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-medium text-brand-dark">Availability Calendar</h1>
          <p className="text-sm text-brand-muted mt-1">
            Rooms available per night. Click a check-in date, then a check-out date, to start a booking.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goPrev}
            disabled={isCurrentMonth}
            className={`btn-outline text-sm px-3 py-1.5 ${isCurrentMonth ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            &larr;
          </button>
          <span className="text-lg font-medium text-brand-dark w-44 text-center">
            {MONTH_NAMES[month]} {year}
          </span>
          <button type="button" onClick={goNext} className="btn-outline text-sm px-3 py-1.5">
            &rarr;
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <Toast message={error} type="error" dismissError={() => setError('')} />
        </div>
      )}

      {/* Room type selector */}
      {categories.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              selectedCategory === 'all'
                ? 'bg-brand-gold text-white border-brand-gold'
                : 'bg-white text-brand-dark border-brand-border hover:border-brand-gold'
            }`}
          >
            All rooms
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-brand-gold text-white border-brand-gold'
                  : 'bg-white text-brand-dark border-brand-border hover:border-brand-gold'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Selection bar */}
      {checkIn && (
        <div className="mb-4 p-4 bg-brand-teal/5 border border-brand-teal/20 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3">
          <p className="text-sm text-brand-dark">
            <span className="font-medium">Check-in:</span> <span className="font-mono">{checkIn}</span>
            {checkOut ? (
              <>
                {' '}&rarr; <span className="font-medium">Check-out:</span> <span className="font-mono">{checkOut}</span>
                <span className="text-brand-muted"> &middot; {nights} night{nights !== 1 ? 's' : ''}</span>
              </>
            ) : (
              <span className="text-brand-muted"> &middot; now select a check-out date</span>
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setCheckIn(''); setCheckOut(''); }}
              className="btn-outline text-sm"
            >
              Clear
            </button>
            {checkOut && (
              <button type="button" onClick={handleSearch} className="btn-gold text-sm">
                Search these dates &rarr;
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-brand-gold border-t-transparent" />
          <p className="mt-3 text-brand-muted text-sm">Loading availability&hellip;</p>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="grid grid-cols-7 border-b border-brand-border bg-gray-50">
              {WEEKDAYS.map((wd) => (
                <div key={wd} className="py-2 text-center text-xs font-medium uppercase tracking-wide text-brand-muted">
                  {wd}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((day, i) => {
                if (day === null) {
                  return <div key={`blank-${i}`} className="border-b border-r border-brand-border/40 min-h-16 md:min-h-20" />;
                }
                const ds = dateStr(year, month, day);
                const isPast = ds < todayStr;
                const avail = availForDay(day);
                const inRange = checkIn && checkOut && ds >= checkIn && ds < checkOut;
                const isCheckIn = ds === checkIn;
                const isCheckOut = ds === checkOut;
                const title = breakdownForDay(day)
                  .map((b) => `${b.name}: ${b.avail ?? '–'}`)
                  .join('\n');
                return (
                  <button
                    key={ds}
                    type="button"
                    onClick={() => handleDayClick(ds, isPast)}
                    disabled={isPast}
                    title={isPast ? undefined : title}
                    className={`relative border-b border-r border-brand-border/40 min-h-16 md:min-h-20 p-1.5 text-left transition-colors
                      ${cellColor(avail, isPast)}
                      ${!isPast ? 'hover:ring-1 hover:ring-inset hover:ring-brand-gold cursor-pointer' : 'cursor-default'}
                      ${inRange || isCheckIn ? 'ring-2 ring-inset ring-brand-gold' : ''}
                      ${isCheckOut ? 'ring-2 ring-inset ring-brand-gold/60' : ''}`}
                  >
                    <span className={`text-xs font-medium ${isPast ? 'text-gray-300' : 'text-brand-dark'}`}>{day}</span>
                    {!isPast && avail !== null && (
                      <span className={`absolute bottom-1.5 right-1.5 text-sm md:text-base font-mono font-medium ${badgeColor(avail)}`}>
                        {avail}
                      </span>
                    )}
                    {isCheckIn && (
                      <span className="absolute top-1 right-1.5 text-[10px] uppercase tracking-wide text-brand-gold font-medium">In</span>
                    )}
                    {isCheckOut && (
                      <span className="absolute top-1 right-1.5 text-[10px] uppercase tracking-wide text-brand-gold font-medium">Out</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-brand-muted">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-green-50 border border-green-200" /> 3+ rooms
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-200" /> 1&ndash;2 rooms
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-200" /> Fully booked
            </span>
            <span>
              {selectedCategory === 'all'
                ? 'Number = total rooms available that night, all room types. Select a room type above to see a specific room.'
                : `Number = ${categories.find((c) => c.id === selectedCategory)?.name || 'selected'} rooms available that night.`}
            </span>
          </div>

          {/* Per-category breakdown for selected check-in day */}
          {checkIn && checkIn.startsWith(`${year}-${pad(month + 1)}`) && (
            <div className="mt-6 card p-5">
              <h2 className="text-sm font-medium text-brand-dark mb-3">
                Availability on <span className="font-mono">{checkIn}</span>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {breakdownForDay(parseInt(checkIn.slice(8), 10)).map((b) => (
                  <div key={b.name} className="bg-gray-50 border border-brand-border rounded-lg p-3">
                    <p className="text-xs text-brand-muted">{b.name}</p>
                    <p className={`text-xl font-mono font-medium ${b.avail === null ? 'text-brand-muted' : badgeColor(b.avail)}`}>
                      {b.avail ?? '–'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Calendar;
